/**
 * Higher-order component that adds streaming visual effects to frame components:
 * - Aurora gradient overlay when status === "streaming"
 *
 * Applied by the shared frame chrome (withFrameChrome), outside the frame body.
 *
 * The wrapper div stays mounted for 800ms after streaming ends so the aurora
 * overlay can fade out gracefully.
 */

import { useState, useEffect, useRef, type ComponentType } from "react";
import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { cn } from "@/lib/cn";
import { AuroraOverlay } from "./aurora-overlay";
import {
  AGENT_TARGET_RING_CLASS,
  agentTargetRingStyle,
  useFrameTargetingColor,
} from "./use-frame-targeting-color";

const SKIP_STREAMING_EFFECTS_FRAME_TYPES = new Set(["terminal"]);

export function withStreamingEffects<P extends FrameProps>(
  FrameComponent: ComponentType<P>,
): ComponentType<P> {
  function FrameWithStreaming(props: P) {
    const supportsStreamingEffects = !SKIP_STREAMING_EFFECTS_FRAME_TYPES.has(
      props.data.frameType,
    );
    const isStreaming =
      supportsStreamingEffects && props.data.status === "streaming";
    // Track "was streaming" to allow aurora fade-out
    const [showEffects, setShowEffects] = useState(false);
    const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Agent targeting: colored ring when an agent cursor targets this frame
    const targetingColor = useFrameTargetingColor(props.id);

    useEffect(() => {
      if (isStreaming) {
        setShowEffects(true);
        if (fadeTimerRef.current) {
          clearTimeout(fadeTimerRef.current);
          fadeTimerRef.current = null;
        }
      } else if (showEffects) {
        // Keep mounted for 800ms fade-out
        fadeTimerRef.current = setTimeout(() => {
          setShowEffects(false);
        }, 800);
      }
      return () => {
        if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
      };
    }, [isStreaming, showEffects]);

    const shouldRenderEffectShell =
      supportsStreamingEffects || Boolean(targetingColor);

    if (!shouldRenderEffectShell) {
      return <FrameComponent {...props} />;
    }

    return (
      <div
        className="relative h-full w-full"
        data-testid={`frame-streaming-wrapper-${props.id}`}
      >
        <FrameComponent {...props} />
        {showEffects && <AuroraOverlay active={isStreaming} />}
        {targetingColor && (
          <div
            className={cn(
              AGENT_TARGET_RING_CLASS,
              "pointer-events-none absolute -inset-px z-20 rounded-frame",
            )}
            style={agentTargetRingStyle(targetingColor)}
            data-testid={`frame-agent-target-${props.id}`}
            aria-hidden="true"
          />
        )}
      </div>
    );
  }

  FrameWithStreaming.displayName = `withStreamingEffects(${
    FrameComponent.displayName ?? FrameComponent.name ?? "Component"
  })`;

  return FrameWithStreaming;
}
